import { useState } from "react";

import { makePostApiCall, makeGetApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import ProgressModal from "../../components/modals/ProgressModal";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";

export function ManufactureEmployeeViewModel(logout, addSnackbar) {
  const [lotNumbers, setLotNumbers] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const getReceivingItemByBarcode = async function (barcode) {
    let callResult = await makeGetApiCall(`receivingItem/${barcode}`);
    return callResult;
  };

  const fetchLotNumbersForDropdown = async function () {
    let callResult = await makeGetApiCall(`lotsForDropdown`);

    if (callResult.success) {
      setLotNumbers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", "Failed to fetch lot numbers");
    }
  };

  const getProductScannedItems = async function (productId) {
    showProgressModal("Fetching scanned items...");
    let callResult = await makeGetApiCall(
      `getProductionScannedItems/${productId}`,
    );
    hideProgressModal();
    if (callResult.success) {
      return callResult.data;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
    return null;
  };

  const saveScannedItemToProduction = async function (payload) {
    console.log("saveScannedItemToProduction payload", payload);
    let callResult = await makePostApiCall(
      `saveScannedItemToProduction`,
      payload,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message
            ? callResult.data.message
            : "Scanned item saved to production",
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
    return null;
  };

  const performDeleteScannedItem = async function (barcode, production_id) {
    let callResult = await makePostApiCall(`deleteScannedItemForProduction`, {
      barcode,
      production_id,
    });

    if (callResult.success) {
      setEmptyModelOpen({
        childComponent: null,
        open: false,
      });
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message
            ? callResult.data.message
            : "Scanned item deleted from production",
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
    return null;
  };

  const askDeleteScannedItem = function (
    barcode,
    scannedItem,
    production_id,
    onSuccess,
  ) {
    const getBarcodeLast9 = (barcode) => {
      if (!barcode) return "N/A";
      return barcode.slice(-9);
    };

    const barcodeDisplay = getBarcodeLast9(barcode);
    const amountUsed = scannedItem?.ingredient_amount_used || 0;
    const unitOfMeasure = scannedItem?.unit_of_measure || "";
    const amountText =
      amountUsed > 0 ? ` (Amount: ${amountUsed} ${unitOfMeasure})` : "";

    const handleConfirm = async () => {
      const result = await performDeleteScannedItem(barcode, production_id);
      if (result && result.success && onSuccess) {
        onSuccess(result);
      }
    };

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={handleConfirm}
          message={`Are you sure you want to delete ${barcodeDisplay}? ${amountText}`}
          setOpen={setEmptyModelOpen}
          title="Delete Scanned Item"
        />
      ),
      open: true,
    });
  };

  const updateScannedItem = async function (payload) {
    let callResult = await makePostApiCall(
      `updateScannedItemForProduction`,
      payload,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message
            ? callResult.data.message
            : "Scanned item updated",
          SnackbarType.Success,
        ),
      );
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Error", callResult.data.message);
    }
    return null;
  };

  return {
    lotNumbers,
    setEmptyModelOpen,
    fetchLotNumbersForDropdown,
    getReceivingItemByBarcode,
    saveScannedItemToProduction,
    getProductScannedItems,
    emptyModal,
    showProgressModal,
    hideProgressModal,
    askDeleteScannedItem,
    updateScannedItem,
  };
}
